import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from "react-toastify";
import { FaCopy, FaShareAlt, FaCheckCircle, FaTimesCircle, FaHourglassHalf } from 'react-icons/fa';
import { getMyReferencesList } from '../../../../redux/modules/profile/profile.action';

const MyReferences = () => {
  const dispatch = useDispatch();
  const profileDetail = useSelector((store) => store.profile.profileDetail);
  const myReferencesList = useSelector((store) => store.profile.myReferencesList);
  const referenceCode = profileDetail?.employeeDTO?.referenceCode;

  useEffect(() => {
    dispatch(getMyReferencesList());
  }, [dispatch]);

  const handleCopy = () => {
    if (referenceCode) {
      navigator.clipboard.writeText(referenceCode)
        .then(() => toast.success('Referans kodunuz kopyalandı!'))
        .catch(() => toast.error('Kopyalama işlemi başarısız oldu.'));
    }
  };

  const handleShare = async () => {
    if (navigator.share && referenceCode) {
      try {
        await navigator.share({
          title: 'Uygulamaya Davet!',
          text: `Referans kodum: ${referenceCode}`,
        });
      } catch (error) {
        console.error('Paylaşım hatası:', error);
      }
    } else {
      toast.error('Tarayıcınız paylaşım özelliğini desteklemiyor. Lütfen kodu kopyalayarak paylaşın.');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'CONFIRM':
        return <FaCheckCircle style={{ color: '#28a745', marginRight: 6, fontSize: 16 }} />;
      case 'WAITING':
        return <FaHourglassHalf style={{ color: '#ffc107', marginRight: 6, fontSize: 16 }} />;
      case 'REJECTED':
        return <FaTimesCircle style={{ color: '#dc3545', marginRight: 6, fontSize: 16 }} />;
      default:
        return null;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'CONFIRM': return 'Onaylandı';
      case 'WAITING': return 'Onay Bekliyor';
      case 'REJECTED': return 'Reddedildi';
      default: return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'CONFIRM': return '#28a745';
      case 'WAITING': return '#ffc107';
      case 'REJECTED': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const references = myReferencesList?.references || [];
  const totalUsers = myReferencesList?.totalUsers || 0;
  const totalEarnedScore = myReferencesList?.totalEarnedScore || 0;

  return (
    <div className="bg-white rounded-3 shadow-sm border border-gray-100 p-4">
      <div className="border-bottom pb-3 mb-3">
        <h4 className="fw-bold mb-0 text-dark">Arkadaşını Getir</h4>
      </div>

      <div className="text-center py-3 px-md-3">
        <div className="mb-2">
          <i className="fas fa-gift text-primary" style={{ fontSize: '36px' }}></i>
        </div>

        <h6 className="fw-bold mb-2">Arkadaşlarınızı davet edin, puan kazanın!</h6>
        <p className="text-muted mb-3" style={{ fontSize: '14px' }}>
          Referans kodunuzu arkadaşlarınızla paylaşın, onlar sizin kodunuzla üye oldukça hem siz hem de arkadaşlarınız puan kazansın.
        </p>

        {referenceCode ? (
          <div className="d-flex flex-column align-items-center">
            <div className="bg-light p-3 rounded-3 mb-3 w-100" style={{ maxWidth: '350px', border: '2px dashed #dee2e6' }}>
              <span className="text-muted d-block mb-1" style={{ fontSize: '13px' }}>Referans Kodum</span>
              <span className="fw-bold text-primary" style={{ fontSize: '28px', letterSpacing: '4px' }}>
                {referenceCode}
              </span>
            </div>

            <div className="d-flex flex-wrap gap-2 justify-content-center mb-3">
              <button
                onClick={handleCopy}
                className="btn btn-outline-primary px-3 py-2 d-flex align-items-center gap-2 fw-bold"
                style={{ fontSize: '14px' }}
              >
                <FaCopy /> Kopyala
              </button>

              <button
                onClick={handleShare}
                className="btn btn-primary px-3 py-2 d-flex align-items-center gap-2 fw-bold"
                style={{ fontSize: '14px' }}
              >
                <FaShareAlt /> Paylaş
              </button>
            </div>
          </div>
        ) : (
          <div className="alert alert-info" role="alert">
            Referans kodunuz oluşturuluyor veya şu anda kullanılamıyor. Lütfen sayfayı yenileyiniz.
          </div>
        )}
      </div>

      {/* Referans Listesi */}
      {references.length > 0 && (
        <div className="mt-2">
          <div className="border-top pt-3">
            <p className="fw-bold text-center mb-3" style={{ fontSize: '15px', color: '#495057' }}>
              Referanslarım: {totalUsers} kişi | Kazanılan: {totalEarnedScore} puan
            </p>

            <div className="table-responsive">
              <table className="table table-bordered table-hover mb-0" style={{ fontSize: '14px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#0d6efd', color: '#fff' }}>
                    <th style={{ padding: '10px 12px', fontWeight: 600 }}>Davet Edilen</th>
                    <th style={{ padding: '10px 12px', fontWeight: 600 }}>Kayıt Tarihi</th>
                    <th style={{ padding: '10px 12px', fontWeight: 600 }}>Durum</th>
                    <th style={{ padding: '10px 12px', fontWeight: 600 }}>Kazanılan Puan</th>
                  </tr>
                </thead>
                <tbody>
                  {references.map((ref, index) => (
                    <tr key={index}>
                      <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>
                        {ref.invitedName}
                      </td>
                      <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>
                        {formatDate(ref.registerDate)}
                      </td>
                      <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>
                        <div className="d-flex align-items-center">
                          {getStatusIcon(ref.status)}
                          <span style={{ color: getStatusColor(ref.status), fontWeight: 500 }}>
                            {getStatusLabel(ref.status)}
                          </span>
                        </div>
                      </td>
                      <td style={{
                        padding: '10px 12px',
                        verticalAlign: 'middle',
                        fontWeight: 600,
                        color: ref.status === 'CONFIRM' && ref.earnedScore && !ref.earnedScore.includes('limit') ? '#28a745' : '#6c757d'
                      }}>
                        {ref.earnedScore}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyReferences;
